import { DOCUMENT } from "@angular/common";
import {Inject, Injectable, NgZone, OnDestroy, signal} from "@angular/core";

const DISMISS_SESSION_KEY = "epak.browserTranslationWarning.dismissed";
const APP_LANG_KEY = "lang";
const POLL_MS = 2000;
const QA_FORCE_PARAM = "translationWarning";

/**
 * CSS selectors for known translation browser extensions.
 * Add new entries here when support reports another extension; keep one selector per line with a comment.
 * We intentionally do not use sessionStorage keys such as google_auto_* (too specific / can linger).
 */
const EXTENSION_DOM_SELECTORS = [
  // XTranslate (Chrome/Firefox) — popup or injected UI on the page
  '[class*="XTranslate"]',
  // Google Translate browser extension — in-page widget / banner frame
  "#google_translate_element",
  ".goog-te-banner-frame",
].join(", ");

/**
 * Detects likely browser or extension translation and drives the warning banner.
 *
 * Does not block translation (not possible from JS). Prevention hints live in index.html:
 * translate="no", class="notranslate", meta google notranslate.
 */
@Injectable({ providedIn: "root" })
export class BrowserTranslationGuardService implements OnDestroy {
  private pollTimer?: ReturnType<typeof setInterval>;
  private monitoringStarted = false;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly ngZone: NgZone,
  ) {}

  readonly warningVisible = signal(false);

  startMonitoring(): void {
    if (this.monitoringStarted) {
      return;
    }
    this.monitoringStarted = true;

    if (this.isQaForceEnabled()) {
      sessionStorage.removeItem(DISMISS_SESSION_KEY);
      this.setDetected(true);
    }

    this.ngZone.runOutsideAngular(() => {
      this.runCheck();
      this.pollTimer = setInterval(() => this.runCheck(), POLL_MS);
    });
  }

  dismissForSession(): void {
    sessionStorage.setItem(DISMISS_SESSION_KEY, "1");
    this.setDetected(false);
  }

  ngOnDestroy(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
    }
  }

  private runCheck(): void {
    if (sessionStorage.getItem(DISMISS_SESSION_KEY) === "1") {
      return;
    }
    if (this.isQaForceEnabled()) {
      this.setDetected(true);
      return;
    }
    this.setDetected(this.isBrowserTranslationActive());
  }

  /**
   * Returns true when any supported signal indicates the page is being machine-translated.
   * Order: built-in browsers first, then extensions.
   */
  private isBrowserTranslationActive(): boolean {
    const html = this.document.documentElement;
    const className = `${html.className} ${this.document.body?.className ?? ""}`;

    // Chrome, Firefox (and some Chromium clones): class on <html> or <body> after "Translate page"
    if (/\btranslated-ltr\b/.test(className) || /\btranslated-rtl\b/.test(className)) {
      return true;
    }

    // Microsoft Edge, Firefox / others: ngx-translate keeps localStorage.lang in sync, but the browser may
    // change document.documentElement.lang when translating (often without translated-ltr)
    if (this.hasAppVsDocumentLangMismatch(html)) {
      return true;
    }

    // Google Translate (built-in or extension using Google) sets this cookie when active
    if (/(?:^|;\s*)googtrans=/.test(this.document.cookie ?? "")) {
      return true;
    }

    // Microsoft Edge Translator: injects these attributes on translated text nodes
    if (this.document.querySelector("[_msthash], [_msttexthash]")) {
      return true;
    }

    // Third-party extensions (XTranslate, Google Translate widget, etc.) — see EXTENSION_DOM_SELECTORS
    if (this.hasTranslationExtensionInDom()) {
      return true;
    }

    return false;
  }

  private hasTranslationExtensionInDom(): boolean {
    return !!this.document.querySelector(EXTENSION_DOM_SELECTORS);
  }

  private hasAppVsDocumentLangMismatch(html: HTMLElement): boolean {
    const appLang = this.primaryLangCode(
      localStorage.getItem(APP_LANG_KEY) ?? sessionStorage.getItem(APP_LANG_KEY),
    );
    const docLang = this.primaryLangCode(html.getAttribute("lang"));
    return !!appLang && !!docLang && appLang !== docLang;
  }

  private primaryLangCode(lang: string | null): string | null {
    return lang?.toLowerCase().split("-")[0] ?? null;
  }

  private isQaForceEnabled(): boolean {
    try {
      return new URLSearchParams(this.document.defaultView?.location.search ?? "").has(QA_FORCE_PARAM);
    } catch {
      return false;
    }
  }

  private setDetected(value: boolean): void {
    if (value === this.warningVisible()) return;
    this.ngZone.run(() => this.warningVisible.set(value));
  }
}
